// frontend/src/pages/EventPlusLandingPage.jsx
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './EventPlusLandingPage.css';

/* 料金は個別見積りのため、CTAはすべてお問い合わせページへ誘導 */
const CONTACT_URL = 'https://orb-rela.com/contact/';

/* 利用の流れ（表面チラシ・裏面チラシ「利用の流れ」より）
   ※アイコンはチラシ裏面「利用の流れ」のイラストをトリミングして使用（/event-plus/flow-*.png） */
const FLOW_STEPS = [
  {
    no: '01',
    title: 'イベントを作成',
    desc: 'イベントを作成し、申し込みURLを送るだけ。名簿がある場合はCSVで一括登録も可能です。',
    image: '/event-plus/flow-1.png',
  },
  {
    no: '02',
    title: '申し込み・QR発行',
    desc: '参加者が申し込むと、QRコードが自動でメール送信されます。',
    image: '/event-plus/flow-2.png',
  },
  {
    no: '03',
    title: '当日はQRで受付',
    desc: 'QRをかざすだけで受付完了。印刷して入館証としても利用できます。',
    image: '/event-plus/flow-3.png',
  },
  {
    no: '04',
    title: '来場状況を確認',
    desc: '入退場の記録を管理画面でリアルタイムに確認できます。',
    image: '/event-plus/flow-4.png',
  },
  {
    no: '05',
    title: '次回イベントの案内',
    desc: '来場者に次回イベントの案内を一括送信し、次の集客につなげます。',
    image: '/event-plus/flow-5.png',
  },
];

/* 主な機能（裏面チラシ「主な機能」より）
   ※チラシ裏面の該当写真をトリミングして使用しています（/event-plus/feature-*.png）。 */
const FEATURES = [
  {
    tag: '申し込み・QR発行',
    title: '申し込みからQR発行までワンストップ',
    desc: '申し込みURLを送るだけで簡単にエントリーを受付。CSVでの一括登録にも対応しています。',
    bullets: [
      '申し込みURLで簡単エントリー',
      'CSVで一括登録',
      'QRコードを自動発行・メール送信',
      '印刷して入館証として利用可能',
    ],
    image: '/event-plus/feature-signup.png',
  },
  {
    tag: '当日の受付',
    title: 'QRをかざすだけの入退場受付',
    desc: '紙の名簿や手作業に頼らない、スピーディーな受付を実現します。',
    bullets: [
      'QRをかざすだけの入退場受付',
      '入退場時刻を自動記録',
      '来場者数をリアルタイム集計',
      '紙の名簿や手作業が不要',
    ],
    image: '/event-plus/feature-checkin.png',
  },
  {
    tag: '来場者管理',
    title: '来場データをまとめて可視化',
    desc: '申し込みから来場までのデータを自動集計。社内共有もスムーズです。',
    bullets: [
      '申し込み一覧・来場者一覧を自動作成',
      '滞在時間や来場のピークを把握',
      'CSVでダウンロード',
      '社内の検証資料や報告書に活用',
    ],
    image: '/event-plus/feature-management.png',
  },
  {
    tag: '次回への活用',
    title: '来場者データを次のアクションへ',
    desc: '一度来場した方への案内を簡単に送信。見込み客リストとして蓄積できます。',
    bullets: [
      '過去の来場者に一括で案内送信',
      '来場者は次回の申し込みが簡単',
      '見込み客リストとして蓄積',
    ],
    image: '/event-plus/feature-followup.png',
  },
];

/* こんなお悩みありませんか？ → イベントPLUSなら解決できます（裏面チラシより） */
const PAIN_SOLUTIONS = [
  {
    pain: '受付に行列ができて、来場者を待たせてしまう',
    solution: '受付がスムーズになり、来場者の満足度が向上',
  },
  {
    pain: '名簿づくりや名札の準備に、毎回時間がかかる',
    solution: '事前準備の手間を大幅に削減',
  },
  {
    pain: '誰が来たのか、当日のチェックが間に合わない',
    solution: 'リアルタイムで来場状況を把握',
  },
  {
    pain: '来場者リストを、次のイベントに活かせていない',
    solution: '来場者データを次回の案内や営業活動に活用',
  },
];

/* 活用シーン（裏面チラシより） */
const USE_SCENES = [
  { title: '展示会・商談会', image: '/event-plus/scene-exhibition.png' },
  { title: 'セミナー・講演会', image: '/event-plus/scene-seminar.png' },
  { title: '社内イベント・研修', image: '/event-plus/scene-internal.png' },
  { title: '説明会・見学会', image: '/event-plus/scene-briefing.png' },
];

/* 導入も安心（裏面チラシより） */
const SUPPORT_ITEMS = [
  {
    title: '専任担当がセットアップ対応',
    desc: '通常数時間で利用開始できます。',
  },
  {
    title: 'PC・スマホどちらからでも利用可能',
    desc: 'ITに不慣れでも安心のサポート体制です。',
  },
];

const Screenshot = ({ src, alt, className = '' }) => {
  const handleError = (e) => {
    e.currentTarget.closest('.eplp__shot')?.classList.add('eplp__shot--empty');
  };

  return (
    <div className={`eplp__shot ${className}`}>
      <img src={src} alt={alt} onError={handleError} loading="lazy" />
      <div className="eplp__shot-placeholder">
        <span>{alt}</span>
      </div>
    </div>
  );
};

const EventPlusLandingPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="eplp">
      <header className="eplp__nav">
        <div className="eplp__container eplp__nav-inner">
          <Link to="/" className="eplp__nav-brand">
            <span className="eplp__nav-logo">
              イベント<strong>PLUS</strong>
            </span>
          </Link>
          <nav className="eplp__nav-links" aria-label="メインナビゲーション">
            <a href="#features">機能</a>
            <a href="#flow">利用の流れ</a>
            <a href="#scenes">活用シーン</a>
            <a href="#support">導入について</a>
          </nav>
          <div className="eplp__nav-actions">
            <Link to="/login" className="eplp__btn eplp__btn--outline">
              ログイン
            </Link>
            <a
              href={CONTACT_URL}
              target="_blank"
              rel="noreferrer"
              className="eplp__btn eplp__btn--primary"
            >
              お問い合わせ
            </a>
          </div>
        </div>
      </header>

      <main>
        {/* ── Hero（表面チラシ） ── */}
        <section className="eplp__hero">
          <div className="eplp__hero-decor" aria-hidden="true">
            <span className="eplp__blob eplp__blob--1" />
            <span className="eplp__blob eplp__blob--2" />
          </div>

          <div className="eplp__container eplp__hero-grid">
            <div className="eplp__hero-copy">
              <span className="eplp__hero-kicker">イベント運営を、もっとスマートに！</span>
              <h1 className="eplp__hero-title">
                受付は、
                <span className="eplp__hero-title-accent">QRをかざすだけ。</span>
              </h1>
              <p className="eplp__hero-sub">
                来場者を「数える」から「活かす」へ。QRコードでイベントの受付を管理し、
                来場者データを次のアクションにつなげるサービスです。
              </p>

              <ul className="eplp__hero-tags">
                <li>展示会</li>
                <li>セミナー</li>
                <li>社内イベント</li>
              </ul>

              <div className="eplp__hero-actions">
                <a
                  href={CONTACT_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="eplp__btn eplp__btn--primary eplp__btn--lg"
                >
                  お問い合わせはこちら
                  <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 0 1 1.414 0l6 6a1 1 0 0 1 0 1.414l-6 6a1 1 0 0 1-1.414-1.414L14.586 11H3a1 1 0 1 1 0-2h11.586l-4.293-4.293a1 1 0 0 1 0-1.414z" clipRule="evenodd" />
                  </svg>
                </a>
                <a href="#features" className="eplp__btn eplp__btn--outline eplp__btn--lg">
                  サービス詳細を見る
                </a>
              </div>

              <ul className="eplp__hero-benefits">
                <li>
                  <span className="eplp__hero-benefit-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M17 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" /><circle cx="10" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                  </span>
                  受付がスムーズに
                </li>
                <li>
                  <span className="eplp__hero-benefit-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 3v18h18" /><path d="M7 15l4-4 3 3 5-6" /></svg>
                  </span>
                  来場者をリアルタイムで把握
                </li>
                <li>
                  <span className="eplp__hero-benefit-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><ellipse cx="12" cy="5" rx="8" ry="3" /><path d="M4 5v6c0 1.66 3.58 3 8 3s8-1.34 8-3V5" /><path d="M4 11v6c0 1.66 3.58 3 8 3s8-1.34 8-3v-6" /></svg>
                  </span>
                  来場者データを次につなげる
                </li>
              </ul>
            </div>

            <div className="eplp__hero-visual">
              <div className="eplp__hero-shot-wrap">
                <Screenshot
                  src="/event-plus/hero-reception.png"
                  alt="QRコードで受付する様子"
                  className="eplp__shot--hero"
                />
              </div>
            </div>
          </div>

          <div className="eplp__wave" aria-hidden="true">
            <svg viewBox="0 0 1440 80" preserveAspectRatio="none">
              <path d="M0,40 C360,80 720,0 1080,40 C1260,60 1380,50 1440,40 L1440,80 L0,80 Z" fill="currentColor" />
            </svg>
          </div>
        </section>

        {/* ── まるごとデジタル化（裏面チラシ・キャッチコピー） ── */}
        <section className="eplp__intro">
          <div className="eplp__container eplp__intro-grid">
            <div className="eplp__intro-copy">
              <h2 className="eplp__intro-title">
                イベントの受付から、その先の活用まで<br />
                まるごとデジタル化。
              </h2>
              <p className="eplp__intro-sub">
                イベントPLUSは、QRコードでイベントの受付を管理し、
                来場者データを次のアクションにつなげるサービスです。
              </p>
            </div>
            <div className="eplp__intro-media">
              <Screenshot
                src="/event-plus/intro-qr.png"
                alt="QRコードをかざして受付する様子"
                className="eplp__intro-shot"
              />
            </div>
          </div>
        </section>

        {/* ── こんなお悩みありませんか？ ── */}
        <section className="eplp__pain eplp__section eplp__section--alt">
          <div className="eplp__container">
            <div className="eplp__pain-columns">
              <div className="eplp__pain-col eplp__pain-col--problem">
                <h3>こんなお悩みありませんか？</h3>
                <ul>
                  {PAIN_SOLUTIONS.map((p) => (
                    <li key={p.pain}>{p.pain}</li>
                  ))}
                </ul>
              </div>

              <div className="eplp__pain-arrow" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <path d="M4 12h16M13 5l7 7-7 7" />
                </svg>
              </div>

              <div className="eplp__pain-col eplp__pain-col--solution">
                <h3>イベントPLUSなら解決できます！</h3>
                <ul>
                  {PAIN_SOLUTIONS.map((p) => (
                    <li key={p.solution}>{p.solution}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ── 利用の流れ ── */}
        <section className="eplp__flow eplp__section" id="flow">
          <div className="eplp__container">
            <div className="eplp__section-head">
              <span className="eplp__eyebrow">利用の流れ</span>
              <h2 className="eplp__section-title">導入から次回集客まで、5つのステップ</h2>
            </div>
            <div className="eplp__flow-row">
              {FLOW_STEPS.map((s, i) => (
                <React.Fragment key={s.no}>
                  <article className="eplp__flow-card">
                    <span className="eplp__flow-icon">
                      <img src={s.image} alt="" aria-hidden="true" />
                    </span>
                    <span className="eplp__flow-no">{s.no}</span>
                    <h3>{s.title}</h3>
                    <p>{s.desc}</p>
                  </article>
                  {i < FLOW_STEPS.length - 1 && (
                    <span className="eplp__flow-connector" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                        <path d="M9 6l6 6-6 6" />
                      </svg>
                    </span>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </section>

        {/* ── 主な機能 ── */}
        <section className="eplp__showcase eplp__section eplp__section--alt" id="features">
          <div className="eplp__container">
            <div className="eplp__section-head">
              <span className="eplp__eyebrow">主な機能</span>
              <h2 className="eplp__section-title">受付から活用まで、必要な機能をひとつに</h2>
              <p className="eplp__section-sub">
                申し込み・QR発行から来場者管理、次回イベントへの活用まで対応します。
              </p>
            </div>

            <div className="eplp__showcase-list">
              {FEATURES.map((f, i) => (
                <div
                  className={`eplp__showcase-row${i % 2 === 1 ? ' eplp__showcase-row--reverse' : ''}`}
                  key={f.title}
                >
                  <div className="eplp__showcase-text">
                    <span className="eplp__showcase-tag">{f.tag}</span>
                    <h3 className="eplp__showcase-title">{f.title}</h3>
                    <p className="eplp__showcase-desc">{f.desc}</p>
                    <ul className="eplp__showcase-bullets">
                      {f.bullets.map((b) => (
                        <li key={b}>{b}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="eplp__showcase-media">
                    <div className="eplp__showcase-media-frame">
                      <Screenshot src={f.image} alt={f.title} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 活用シーン ── */}
        <section className="eplp__scenes eplp__section" id="scenes">
          <div className="eplp__container">
            <div className="eplp__section-head">
              <span className="eplp__eyebrow">活用シーン</span>
              <h2 className="eplp__section-title">こんなシーンで選ばれています</h2>
            </div>
            <div className="eplp__scenes-grid">
              {USE_SCENES.map((s) => (
                <figure className="eplp__scene-card" key={s.title}>
                  <Screenshot src={s.image} alt={s.title} className="eplp__scene-shot" />
                  <figcaption>{s.title}</figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* ── 導入も安心 ── */}
        <section className="eplp__support eplp__section eplp__section--alt" id="support">
          <div className="eplp__container">
            <div className="eplp__section-head">
              <span className="eplp__eyebrow">導入も安心</span>
              <h2 className="eplp__section-title">はじめてのイベントDXも、しっかりサポート</h2>
            </div>
            <div className="eplp__support-row">
              {SUPPORT_ITEMS.map((s) => (
                <article className="eplp__support-card" key={s.title}>
                  <span className="eplp__support-check" aria-hidden="true">✓</span>
                  <div>
                    <h3>{s.title}</h3>
                    <p>{s.desc}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA banner ── */}
        <section className="eplp__cta">
          <div className="eplp__container">
            <div className="eplp__cta-box">
              <h2 className="eplp__cta-title">イベントの可能性を、もっとプラスに。</h2>
              <p className="eplp__cta-sub">
                料金・導入時期・機能のご相談など、まずはお気軽にお問い合わせください。
              </p>
              <a
                href={CONTACT_URL}
                target="_blank"
                rel="noreferrer"
                className="eplp__btn eplp__btn--white eplp__btn--lg"
              >
                お問い合わせはこちら
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="eplp__footer">
        <div className="eplp__container eplp__footer-top">
          <div className="eplp__footer-brand">
            <span className="eplp__footer-logo">
              イベント<strong>PLUS</strong>
            </span>
            <p>イベントの受付から、その先の活用まで。まるごとデジタル化。</p>
          </div>

          <div className="eplp__footer-col">
            <h4>サービス</h4>
            <a href="#features">機能</a>
            <a href="#flow">利用の流れ</a>
            <a href="#scenes">活用シーン</a>
            <a href={CONTACT_URL} target="_blank" rel="noreferrer">お問い合わせ</a>
          </div>

          <div className="eplp__footer-col">
            <h4>会社情報</h4>
            <span>株式会社Orb</span>
            <span>〒710-0842</span>
            <span>岡山県倉敷市吉岡242-1</span>
            <a href="tel:0864415760">TEL 086-441-5760</a>
            <a href="https://orb-rela.com/" target="_blank" rel="noreferrer">
              https://orb-rela.com/
            </a>
          </div>
        </div>
        <div className="eplp__container eplp__footer-bottom">
          <span>© {new Date().getFullYear()} 株式会社Orb イベントPLUS</span>
        </div>
      </footer>
    </div>
  );
};

export default EventPlusLandingPage;
